import type { IBatchStore } from './batch-store-types';
import logger from '../../utils/logger';
import { timerKey } from '../../utils/timer-manager/timer-manager';
import { DeviceStatus } from '../../constants/DeviceStatus';
import { toNumber } from '../../utils/numbers';
import {
  BatchAction,
  BatchMode,
  MaterialType,
  MeteredMaterialType,
} from '../../constants/MaterialModels';
import convertUnit from '../../utils/unit-converter';
import {
  getWaterPump,
  getSecondWaterPump,
} from './water-pump-checks';
import type {
  IPump,
  ITank,
} from '../../../types/device/DeviceModels';
import type { MixDesignItem } from '../../../types/batch/MixDesignItem';
import type {
  IMaterial,
  IMaterialWithDetails,
} from '../../../types/material/MaterialModels';
import type { IBatchData } from '../../containers/Batch/IBatchData';
import type {
  IMeteredDataToBatch,
  IWeighedDataToBatch,
} from '../../../types/batch/DataToBatch';
import { waterShouldWaitForMoistureProbe } from './live-moisture';
import { isBatchStartPercentCorrect } from './checkAdmixtureDischargeConditions';
import {
  calculateAdjustedTargetForDisplay,
  handlePrecisionLoss,
} from './precision-loss-handler';
import type { DischargeScale } from '../../../types/api/Discharge';
import { ScaleTypes } from '../discharge-store';

export const pumpMeteredMixin = {

  getPumpWeight(this: IBatchStore, pump: IPump) {

    if (!pump.meterPNo) {
      return 0;
    }

    return toNumber(this.meters[pump.meterPNo - 1], 2);
  },

  getTotalWeightInActiveLoad(this: IBatchStore, device: IPump) {

    const deviceStatus = this.getDeviceStatus(device.id);
    const {
      uid,
      currentDrop,
      weight: currentDeviceWeight,
    } = deviceStatus;
    const material = this.materialStore.materials.find((v: IMaterial) => v.pumpId === device.id);

    if (!uid) {
      return 0;
    }

    let totalWeight = 0;
    const currentLoad = this.running.find(load => load.uid === uid);
    if (!currentLoad?.batchDrops) {
      return 0;
    }

    if (
      !currentLoad.batchDrops[currentDrop] ||
      !material ||
      !currentLoad.batchDrops[currentDrop].some((dropData: any) => dropData.constituentItemId === material?.itemId)
    ) {
      // Currently batched drop data for this material hasn't been registered, yet
      // It means that the drop is in progress, and we can include only its dynamic weight reading
      // from the device status:
      totalWeight = currentDeviceWeight;
    }

    // Add weight of all the registered drops for this load
    for (const drops of Object.values(currentLoad.batchDrops)) {
      // @ts-ignore
      const drop: any = drops.find((drop: any) => drop.constituentItemId === material?.itemId);
      if (drop) {
        let totalWeightInThisDrop = drop.actualWeight;
        if (device.unitId) {
          totalWeightInThisDrop = convertUnit(drop.actualWeight, drop.unitSymbol, this.getUnitById(device.unitId));
        }
        totalWeight += totalWeightInThisDrop;
      }
    }

    return toNumber(totalWeight, 2);
  },

  getTargetForMetered(
    this: IBatchStore,
    material: IMaterial,
    materialDetails: IMaterialWithDetails,
    constituent: MixDesignItem,
    data: IBatchData,
    withDropSize: boolean = true,
  ): number {

    let target = 0;
    let qty;
    const dropSize = withDropSize ? data.dropSize : 1;

    if (constituent) {
      qty = typeof constituent.quantityNet === 'undefined'
        ? constituent.quantity
        : constituent.quantityNet;
    } else {
      qty = data.quantity;
    }

    if (materialDetails.type === MeteredMaterialType.Admixture || materialDetails.type === MeteredMaterialType.Water
      || materialDetails.type === MeteredMaterialType.SecondWater) {
      target = toNumber(qty * dropSize, 2);
    } else {
      logger.debug(`Unknown type: ${materialDetails.type} for ${material.code}`);
    }

    return toNumber(target, 2) || 0;
  },

  hasPumpFinished(this: IBatchStore, pump: IPump) {

    if (!pump.meterPNo) {
      return false;
    }

    return Number(this.metersStatus[pump.meterPNo - 1]) > 0;
  },

  isInputPortOff(this: IBatchStore, port: number): boolean {

    if (!port || port < 0) {
      return false;
    }

    return Number(this.inputs[port - 1]) === 0;
  },

  isPumpNotEmpty(this: IBatchStore, pump: IPump): boolean {

    return this.isInputPortOff(pump.emptyStatusPNo);
  },

  isPumpEmpty(this: IBatchStore, pump: IPump): boolean {

    return !this.isPumpNotEmpty(pump);
  },

  isTankClosed(this: IBatchStore, tank: ITank): boolean {

    return !this.isInputPortOff(tank.closeStatusPNo);
  },

  isTankEmpty(this: IBatchStore, tank: ITank): boolean {

    return !this.isInputPortOff(tank.emptyStatusPNo);
  },

  getUnitById(this: IBatchStore, unitId: number) {

    return this.materialStore.getUnitSymbolByUnitId(unitId);
  },

  isBottlePump(this: IBatchStore, pump: IPump): boolean {

    return !!(pump?.bottleGatePNo && pump.emptyStatusPNo);
  },

  isDirectFeedPump(this: IBatchStore, pump: IPump, materialDetails: IMaterialWithDetails): boolean {
    return materialDetails.type === MeteredMaterialType.Admixture
      && !!pump?.gatePNo && !pump.bottleGatePNo;
  },

  isPumpWithTank(this: IBatchStore, pump: IPump): boolean {

    return !!(pump?.tankId);
  },

  restartHeadWaterPump(this: IBatchStore, data: IBatchData) {

    const waterPumpData = data.pumps
      && Object.values(data.pumps)
        .find((v: any) => v.headWaterStartedManually && !v.headWaterDischarged);

    if (waterPumpData?.pump) {
      const pumpStatus = this.getDeviceStatus(waterPumpData.pump.id);
      if (pumpStatus.status === DeviceStatus.WAIT_MOIST) {
        this.startHeadWater(waterPumpData.pump, pumpStatus.weight);
      }
    }
  },

  startHeadWater(this: IBatchStore, pump: IPump, initialPumpValue: number|null = null) {

    const pumpStatus = this.getDeviceStatus(pump.id);
    const data = this.running.find(v => v.uid === pumpStatus.uid && v.currentDrop === pumpStatus.currentDrop);
    if (!data) {
      logger.log('startHeadWater, no running data for pump', pump.description, pumpStatus);
      return;
    }
    const pumpData = data?.pumps ? data.pumps[pump.id] : null;

    if (data && pumpData?.headWater && !pumpData?.headWaterDischarged) {
      pumpData.headWaterStartedManually = true;
      logger.log('pump press: start head water', pump.id, 'headWater:', pumpData.headWater, 'headWaterStarted:', pumpData.headWaterStarted);

      this.batchMetered(pumpData, data, initialPumpValue, () => {
        const d = this.getDeviceStatus(pump.id);
        if (data.headWater && !pumpData.headWaterDischarged) {
          pumpData.headWaterActual = d.weight;

          if (waterShouldWaitForMoistureProbe(data, this)) {
            this.deviceStatus.set(pump.id, {
              ...d,
              status: DeviceStatus.WAIT_MOIST,
            });

            if (this.isPumpWithTank(pump)) {
              this.deviceStatus.set(pump.tankId, {
                ...this.getDeviceStatus(pump.tankId),
                uid: data.uid,
                tId: data.tId,
                currentDrop: data.currentDrop,
                status: DeviceStatus.WAIT_MOIST,
              });
            }

          } else {
            data.headWaterPumpDischarged = true;
            pumpData.headWaterDischarged = true;

            this.deviceStatus.set(pump.id, {
              ...d,
              status: pumpData.washWater ? DeviceStatus.WAIT_WATER : DeviceStatus.FINISH,
            });

            if (!this.isPumpWithTank(pump)) {
              this.resumePrimaryScaleDischarge(data);
            }

            this.checkScalesIfWaitingForWater(data);

            if (!pumpData.washWater) {
              pumpData.isDischarged = true;
            }
          }
        }
      });
    }
  },

  startWashWaterButtonShouldBeVisible(this: IBatchStore, pump: IPump): boolean {
    const pumpStatus = this.getDeviceStatus(pump.id);
    const data = this.running.find(v => v.uid === pumpStatus.uid && v.currentDrop === pumpStatus.currentDrop);

    if (!data || pump?.tankId) {
      return false;
    }

    const pumpData = data?.pumps ? data.pumps[pump.id] : null;

    return [DeviceStatus.WAIT_WATER, DeviceStatus.WAIT_DISCH].includes(pumpStatus.status)
      && pumpData?.washWater
      && !pumpData?.washWaterDischarged
      && (
        (pumpData?.headWater && pumpData?.headWaterDischarged)
        ||
        (!pumpData?.headWater)
      );
  },

  startWashWater(this: IBatchStore, pump: IPump) {
    const pumpStatus = this.getDeviceStatus(pump.id);
    const data = this.running.find(v => v.uid === pumpStatus.uid && v.currentDrop === pumpStatus.currentDrop);

    if (!data) {
      logger.log('startWashWater, no running data for pump', pump?.description, pumpStatus);
      return;
    }

    const pumpData = data?.pumps ? data.pumps[pump.id] : null;

    if (this.startWashWaterButtonShouldBeVisible(pump)) {
      logger.log('wash water started manually', pump?.description);
      pumpData.washWaterReadyForDischarge = true;

      this.batchMetered(pumpData, data, null, () => {
        this.onFinishNonBottlePump(pumpData, data, null);
      });
    }
  },

  startExtraWater(
    this: IBatchStore,
    batchData: IBatchData,
    waterId: number,
    waterTarget: number,
  ): void {

    if (!batchData || !this.isActiveDrop(batchData)) {
      return;
    }

    const material = this.materialStore.getMaterialByItemId(waterId);
    if (!material) {
      return;
    }

    if (material.assignment === MaterialType.Weighed) {
      let weighedData: IWeighedDataToBatch | any = null;
      Object.keys(batchData.scales).forEach(scaleId => {
        weighedData = batchData.scales[scaleId].find((s: IWeighedDataToBatch) => s.material.itemId === waterId);
      });
      if (weighedData) {
        const { scale, materialDetails } = weighedData;
        const batchUnit = materialDetails.itemBatchUnitSymbol;
        const deviceUnit = this.getUnitById(scale.unitId);
        const isDifferentUnit = batchUnit !== deviceUnit;
        const convertedTarget = isDifferentUnit
          ? toNumber(convertUnit(waterTarget, batchUnit, deviceUnit), 5)
          : waterTarget;
        weighedData.extraWater = waterTarget;
        weighedData.extraWaterStarted = true;
        weighedData.isFeedFinish = false;
        weighedData.mft = convertedTarget;
        batchData.finalScalesDischarged = batchData.finalScalesDischarged.filter(scaleId => scaleId !== scale.id);
        const waterScale = batchData.sequence.discharges.find((v: DischargeScale) => v.type === ScaleTypes.WATER);
        if (waterScale) {
          waterScale.dischargeFinish = false;
        }
        this.batchMaterial(weighedData, batchData);
      }
      return;
    }

    const pumpData = batchData.pumps[material.pumpId];
    if (!pumpData) {
      return;
    }

    pumpData.extraWater = waterTarget;
    const { pump, materialDetails, extraWater } = pumpData;
    const materialCode = this.materialStore.getMaterialName(material);
    const unit = materialDetails.itemBatchUnitSymbol || '';
    const meterRate = materialDetails.meterRate || 1;
    pumpData.mft += extraWater;
    pumpData.isDischarged = false;
    pumpData.extraWaterStarted = true;
    const initialPumpValue = this.getPumpWeight(pump);
    let target = toNumber((extraWater - materialDetails.cutOff) / meterRate);

    this.setMeter(pump.meterPNo, initialPumpValue + target, pump.gatePNo);
    this.checkSetMeterCommandSent(pump, initialPumpValue + target);

    const s = {
      target: pumpData.mft,
      unit: unit,
      mCode: batchData.mix ? batchData.mix.itemCode : materialCode,
      materialCode,
      currentDrop: batchData.currentDrop,
      allDrops: batchData.allDrops,
      uid: batchData.uid,
      tId: batchData.tId,
      error: false,
    };

    this.deviceStatus.set(pump.id, {
      ...this.getDeviceStatusDefault(pump),
      ...s,
      status: DeviceStatus.FEED,
    });

    if (pump.tankId) {
      const tank = this.devicesStore.getDeviceById(pump.tankId);
      this.deviceStatus.set(pump.tankId, {
        ...this.getDeviceStatusDefault(tank),
        ...s,
        status: DeviceStatus.FEED,
      });
    }

    this.checkMeteredWeight(pumpData, target, initialPumpValue, batchData);

    this.checkPumpFinishFeed(pumpData, batchData, (pumpData: IMeteredDataToBatch, batchData: IBatchData) => {
      this.onPumpFinishFeed(pumpData, batchData, initialPumpValue, () => {
        this.onFinishNonBottlePump(pumpData, batchData, null);
      });
    });
  },

  batchMetered(
    this: IBatchStore,
    pumpData: IMeteredDataToBatch,
    data: IBatchData,
    initialPumpValue: number|null = null,
    onFinish: (pumpData?: IMeteredDataToBatch, batchData?: IBatchData) => void = () => {/* empty */},
  ) {

    let {
      pump,
      material,
      mft,
      isDischarged,
      headWater,
      headWaterStarted,
      headWaterActual: hwa,
      headWaterDischarged,
      washWater,
      washWaterActual,
      washWaterReadyForDischarge,
      washWaterDischarged,
      initialWaterValue,
    } = pumpData;
    const headWaterActual = headWaterDischarged && !hwa
      ? this.getPumpWeight(pump) - initialWaterValue
      : hwa;
    const waterPump = getWaterPump(this.rootStore, data) || {};
    const secondWaterPump = getSecondWaterPump(this.rootStore, data) || {};
    const materialDetails = this.materialStore.materialDetails[material.materialDetailId];
    const materialCode = this.materialStore.getMaterialName(material);
    const unit = materialDetails.itemBatchUnitSymbol || '';
    const meterRate = materialDetails.meterRate || 1;
    let targetWithoutRounding = (mft - materialDetails.cutOff) / meterRate;
    let target = toNumber(targetWithoutRounding) || 1;

    const d = this.getDeviceStatus(pump.id);

    if (this.isAbort || !this.isActiveDrop(data)) {
      return;
    }

    if (isDischarged || (d.status === DeviceStatus.FINISH && headWaterDischarged && washWaterDischarged)) {
      logger.log('pump already discharged:', material.code, pump.id, d.status);
      return;
    }

    if (!this.canBatchMetered(data, pumpData)) {
      return;
    }

    if (materialDetails.type === MeteredMaterialType.Admixture
      && [
        BatchAction.FineAggStart,
        BatchAction.ScaleAggDischargeStart,
        BatchAction.ScaleCemDischargeStart,
        BatchAction.ScaleWaterDischargeStart,
      ].includes(materialDetails.batchStart)
      && materialDetails.batchStartPercent > 0
      && !isBatchStartPercentCorrect(this.rootStore, data, pumpData)
    ) {
      logger.log('batch metered batchStartPercent not ready:', material.code, pump.id);
      return;
    }

    if (materialDetails.type === MeteredMaterialType.Admixture
      && !this.isAdmixturePriorityReady(data, pumpData)
      && !this.isBottlePump(pump)
    ) {
      logger.log('batch metered not ready, wait priority:', material.code, pump.id);
      return;
    }

    logger.log('batch metered:', material.code, pump.id, d.status, `${data.uid}: ${data.currentDrop}/${data.allDrops}`,
      'head water discharged:', headWaterDischarged, 'wash water discharged:', washWaterDischarged);

    if (data.allDrops > 1) {
      const prevDrop = this.running.find(v => v.uid === data.uid && v.currentDrop === data.currentDrop - 1);
      const prevPumpData = prevDrop ? prevDrop.pumps[pump.id] : null;

      if (prevPumpData && !prevPumpData.isDischarged) {
        logger.log('prev pump data not finish:', pump.description, `${prevDrop.uid}: ${prevDrop.currentDrop}/${prevDrop.allDrops}`);
        return;
      }
    }

    if (d.status === DeviceStatus.FEED || d.status === DeviceStatus.SETTLING
      || (headWater && headWaterStarted && !headWaterDischarged
        && d.status !== DeviceStatus.HOLD && d.status !== DeviceStatus.MIN_FLOW_RATE && d.status !== DeviceStatus.WAIT_MOIST)) {
      logger.log('batch metered in progress:', material.code, d.status, 'headWaterStarted:', headWaterStarted);
      return;
    }

    if (d.status === DeviceStatus.WAIT_MOIST && waterShouldWaitForMoistureProbe(data, this)) {
      logger.log('pump wait for live moisture adjustment', material.code, pump.description, d.status);
      return;
    }

    if (waterPump.id === pump.id || secondWaterPump.id === pump.id) {

      if (pump.tankId) {
        this.timers.clear(timerKey.batchMetered(pump.tankId));
        const tankStatus = this.getDeviceStatus(pump.tankId);

        if (d.status !== DeviceStatus.HOLD
          && [DeviceStatus.DISCHARGING, DeviceStatus.FEED, DeviceStatus.WAIT_DISCH, DeviceStatus.CLOSING].includes(tankStatus.status)
          && !(waterPump.id && secondWaterPump.id)
        ) {
          logger.log(`tank #${pump.tankId} not ready`, tankStatus.status, pump.description);

          this.timers.set(timerKey.batchMetered(pump.tankId), () => {
            this.batchMetered(pumpData, data, initialPumpValue, onFinish);
          }, 2000, `Batch metered - tank #${pump.tankId}`);

          return;
        }

        if (headWaterDischarged && !data.headWaterTankDischarged) {
          logger.log(`tank #${pump.tankId} head water not discharged yet!!!`);
          return;
        }

      }

      if (!pump.tankId && headWaterDischarged && !washWater) {
        logger.log(`water pump ${pump.description}: head water discharged and no wash water!`);
        return;
      }

      if (data.headWater && !headWaterDischarged) {

        if (!pump.tankId && data.mixerId && !this.mixerIsReadyForDischarge(data)) {
          logger.log(`mixer not ready for water pump ${pump.description}
           material=${material.code} load=${data.uid} drop=${data.currentDrop}`);

          this.timers.set(timerKey.batchMetered(pump.id), () => {
            this.batchMetered(pumpData, data, initialPumpValue, onFinish);
          }, 2000, `Batch metered - water pump ${pump.description} material=${material.code} load=${data.uid} drop=${data.currentDrop}`);
          return;
        }

        if (!pump.tankId && !this.isDischargeAdmixBeforeHeadWaterCompleted(data)) {
          logger.log(`head water not ready, water pump ${pump.description}
           material=${material.code} load=${data.uid} drop=${data.currentDrop} waiting for admix`);

          const conflictedAdmixWithWater = this.getConflictedAdmixWithWater(data, true, false);
          if (conflictedAdmixWithWater) {
            logger.warn(`discharge sequence conflict - water pump wait for admix ${conflictedAdmixWithWater.itemCode},
             forcing pump to discharge head water`);

            this.showError(`Discharge sequence conflict - Water is waiting for admix
            ${conflictedAdmixWithWater.itemCode} while ${conflictedAdmixWithWater.itemCode} is waiting for the water`);

          } else {

            this.timers.set(timerKey.batchMetered(pump.id), () => {
              this.batchMetered(pumpData, data, initialPumpValue, onFinish);
            }, 2000, `Batch metered - water pump ${pump.description} material=${material.code} load=${data.uid} drop=${data.currentDrop}`);
            return;
          }
        }

        target = toNumber((headWater - hwa - materialDetails.cutOff) / meterRate) || 1;
        targetWithoutRounding = (headWater - hwa - materialDetails.cutOff) / meterRate;
        logger.log('discharging head water for water pump', material.code, headWater, data.headWater, materialDetails.cutOff, target);
      } else if (data.washWater && !washWaterDischarged) {
        if (data.finalScales && Object.keys(data.finalScales).length !== data.finalScalesDischarged.length && !pump.tankId) {
          if (!washWaterReadyForDischarge) {
            logger.log('wash water not ready, waiting for scales to finish discharge or washStart %');
            return;
          }
        }

        if (!pump.tankId && !this.isDischargeAdmixBeforeWashWaterCompleted(data)) {
          logger.log('wash water not ready, waiting for admix');

          const conflictedAdmixWithWater = this.getConflictedAdmixWithWater(data, false, true);
          if (conflictedAdmixWithWater) {
            logger.warn(`discharge sequence conflict - water pump wait for admix ${conflictedAdmixWithWater.itemCode},
             forcing pump to discharge wash water`);

            this.showError(`Discharge sequence conflict - Water is waiting for admix
            ${conflictedAdmixWithWater.itemCode} while ${conflictedAdmixWithWater.itemCode} is waiting for the water`);

          } else {
            this.timers.set(timerKey.batchMetered(pump.id), () => {
              this.batchMetered(pumpData, data, initialPumpValue, onFinish);
            }, 2000, `Batch metered - pump #${pump.id}`);
            return;
          }
        }

        if (headWaterActual !== pumpData.headWaterActual) {
          // update head water to display on the ticket,
          // in case there was PAC issue and meter status was on at the beginning of the feed
          pumpData.headWaterActual = headWaterActual;
        }

        let overTarget = 0;

        if (data.currentDrop > 1 && data.currentDrop === data.allDrops) {
          overTarget = this.getOverTargetFromPrevDrops(pumpData.material.itemId, data);
        }

        const adjustedMft = Math.max(0, mft - overTarget - headWaterActual - washWaterActual - materialDetails.cutOff);
        targetWithoutRounding = adjustedMft / meterRate;
        target = toNumber(targetWithoutRounding) || 1;
        logger.log('discharging wash water for water pump', material.code, target, overTarget);
      }
    }

    if (this.isAbort || !this.isActiveDrop(data)) {
      return;
    }

    this.timers.clear(timerKey.batchMetered(pump.id));

    if (d.status === DeviceStatus.METER_ERROR) {
      this.timers.set(timerKey.batchMetered(pump.id), () => {
        this.batchMetered(pumpData, data, initialPumpValue, onFinish);
      }, 2000, `Batch metered retry - pump #${pump.id}`);

      return;
    }

    if (
      this.isHoldFeed
      || data.isHoldLoad
      || this.isHoldFeedDevice(pump.id)
      || (this.isDirectFeedPump(pump, materialDetails) && this.isHoldDischarge)
    ) {
      initialPumpValue = initialPumpValue ?? this.getPumpWeight(pump);
      const weight = this.getPumpWeight(pump) - (initialPumpValue || 0);
      const actualWeight = headWaterActual + (weight * meterRate);
      const d = this.getDeviceStatus(pump.id);

      this.deviceStatus.set(pump.id, {
        ...d,
        unit,
        weight: actualWeight,
      });

      if (this.isFinishFeedDevice(pump.id)) {
        this.timers.clear(timerKey.batchMetered(pump.id));
        this.deviceStatus.set(pump.id, {
          ...d,
          error: false,
          status: DeviceStatus.SETTLING,
          unit,
          weight: actualWeight,
        });

        pumpData.isFeedInterrupted = true;

        this.onPumpFinishFeed(pumpData, data, initialPumpValue, () => {
          this.finishFeedDevices = this.finishFeedDevices.filter(id => id !== pump.id);
          this.unHoldFeedDevice(pump.id);
          data.pumps[pump.id].isDischarged = true;

          if (data.mixDesign) {
            const dropIndex = this.running.findIndex(v => v._id === data._id);

            if (dropIndex >= 0) {
              this.running[dropIndex].mixDesign.forEach((v: MixDesignItem, i: number) => {
                if (v.constituentItemId === material.itemId) {
                  this.running[dropIndex].mixDesign[i].manuallyTerminated = true;
                  logger.log('pump manuallyTerminated', material.code);
                }
              });
            }
          }

          this.deviceStatus.set(pump.id, {
            ...d,
            error: false,
            status: DeviceStatus.FINISH,
          });

          if (
            materialDetails.type === MeteredMaterialType.Admixture
            && materialDetails.priority
          ) {
            logger.log('trigger batchStart event, after admix with priority finish',
              material.code, materialDetails.priority, materialDetails.batchStart);
            this.onBatchAction(data, materialDetails.batchStart);
          }

          if (data.materialId) {
            if (data.mixerId) {
              this.dischargeMixer(data);
            } else {
              this.finishDischarge(data);
            }
          }

        });

        return;
      }

      this.timers.set(timerKey.batchMetered(pump.id), () => {
        this.batchMetered(pumpData, data, initialPumpValue, onFinish);
      }, 2000, `Batch metered retry - pump #${pump.id}`);

      return;
    }

    if (pump.tankId && initialPumpValue === null) {
      const t = this.getDeviceStatus(pump.tankId);

      if (t.status !== DeviceStatus.WAIT_MOIST) {

        const tank = this.devicesStore.getDeviceById(pump.tankId);
        const tankStatus = this.getDeviceStatusDefault(tank);

        if (tankStatus.status !== DeviceStatus.IDLE && tankStatus.status !== DeviceStatus.FINISH) {
          logger.log(`tank #${pump.tankId} not ready`, tankStatus.status);
          return;
        }

        if (!this.isTankEmpty(tank) && !this.isOverride) {
          this.deviceStatus.set(pump.tankId, {
            ...this.getDeviceStatusDefault(tank),
            id: Number(pump.tankId),
            status: DeviceStatus.NOT_EMPTY,
            error: true,
          });

          this.showWarning({ key: 'Tank is not empty!', props: { tankName: tank.description } });

          this.checkTankIsEmpty(tank, pumpData, data, () => {
            this.batchMetered(pumpData, data, initialPumpValue, onFinish);
          });

          return;
        }

        if (!this.isTankClosed(tank) && !this.isOverride) {
          this.deviceStatus.set(pump.tankId, {
            ...this.getDeviceStatusDefault(tank),
            id: Number(pump.tankId),
            status: DeviceStatus.OPEN_ERROR,
            error: true,
          });

          this.showWarning({ key: 'Tank is open!', props: { tankName: tank.description } });

          this.checkTankIsClosed(tank, data, () => {
            this.batchMetered(pumpData, data, initialPumpValue, onFinish);
          });

          return;
        }
      }
    }

    if (pumpData.initialValue === null) {
      pumpData.initialValue = this.getPumpWeight(pump);
    }

    if (pumpData.headWater && !pumpData.headWaterStarted) {
      pumpData.headWaterStarted = true;
      pumpData.initialWaterValue = this.getPumpWeight(pump);
    }

    let batchDelay = materialDetails.batchDelay || 0;

    if (pumpData.washWater && !pumpData.washWaterDischarged && !pump.tankId) {
      batchDelay = materialDetails.dischargeDelay || 0;
    }

    if ((pumpData.headWater || pumpData.washWater) && pump.tankId) {
      batchDelay = 0;
    }

    const status = materialDetails.batchMode === BatchMode.Manual
      ? DeviceStatus.MANUAL
      : materialDetails.batchMode === BatchMode.Skip
        ? DeviceStatus.SKIP
        : DeviceStatus.DELAY;

    const s = {
      target: mft,
      unit: unit,
      status,
      mCode: data.mix ? data.mix.itemCode : materialCode,
      materialCode,
      currentDrop: data.currentDrop,
      allDrops: data.allDrops,
      uid: data.uid,
      tId: data.tId,
      error: false,
    };

    this.deviceStatus.set(pump.id, {
      ...this.getDeviceStatus(pump.id),
      ...s,
      id: pump.id,
    });

    if (pump.tankId) {
      const tank = this.devicesStore.getDeviceById(pump.tankId);
      this.deviceStatus.set(pump.tankId, {
        ...this.getDeviceStatusDefault(tank),
        ...s,
        id: Number(pump.tankId),
      });
    }

    if (materialDetails.batchMode === BatchMode.Skip) {
      logger.log(`${material.code} batch mode: SKIP`);
      this.showWarning(`SKIP ${materialCode}`);

      this.saveTargetForSkipMaterial(data, material.itemId, mft);
      pumpData.isDischarged = true;

      onFinish(pumpData, data);
      return;
    }

    if (this.isTestMode) {
      this.checkPumpInTestMode(pumpData, data);
      return;
    }

    // Precision loss will be handled when pump actually starts feeding
    logger.log('batchMetered:', materialCode, pump.id, 'mft:', mft, 'cutoff:', materialDetails.cutOff, 'target:', target, 'delay:', batchDelay);

    if (this.hasPumpFinished(pump)) {
      this.resetMeter(pump.meterPNo);
    }

    this.timers.set(timerKey.batchMetered(pump.id), () => {
      initialPumpValue = initialPumpValue ?? this.getPumpWeight(pump);

      // Handle precision loss if enabled
      const roundOffMeteredEnabled = this.settingsStore.configuration?.roundOffMetered;
      const runningBatch = this.running.find(r => r.uid === data.uid);

      if (
        runningBatch &&
        roundOffMeteredEnabled &&
        // Due to differences in how target for water is calculated
        // (head water processed as a separate target during the same drop)
        // Waters are, for now, disabled for precision loss calculation
        materialDetails.type !== MeteredMaterialType.Water &&
        materialDetails.type !== MeteredMaterialType.SecondWater
      ) {
        // Calculate precision loss and get adjustment
        const precisionLoss = targetWithoutRounding - target;
        const precisionLossAdjustment = handlePrecisionLoss(
          data.uid,
          pump.id,
          precisionLoss,
          pumpData,
        );

        target += precisionLossAdjustment;

        // Calculate and set adjusted target for display
        const adjustedTargetForDisplay = calculateAdjustedTargetForDisplay(
          target,
          0, // adjustment already included in target
          materialDetails.cutOff,
          meterRate,
        );

        this.deviceStatus.set(pump.id, {
          ...this.getDeviceStatus(pump.id),
          adjustedTargetForDisplay,
        });
      }

      if (materialDetails.batchMode === BatchMode.Auto) {
        this.setMeter(pump.meterPNo, initialPumpValue + target, pump.gatePNo);
        this.checkSetMeterCommandSent(pump, initialPumpValue + target);

        this.deviceStatus.set(pump.id, {
          ...this.getDeviceStatus(pump.id),
          status: DeviceStatus.FEED,
        });

        if (pump.tankId) {
          this.deviceStatus.set(pump.tankId, {
            ...this.getDeviceStatus(pump.tankId),
            status: DeviceStatus.FEED,
          });
        }
      }

      if ((waterPump.id === pump.id || secondWaterPump.id === pump.id) && !pump.tankId) {
        if (!data.headWaterPumpDischarged && data.headWater) {
          this.onBatchAction(data, BatchAction.StartDischargingHeadWater);
        } else if (!data.washWaterPumpDischarged && data.washWater) {
          this.onBatchAction(data, BatchAction.StartDischargingWashWater);
        }
      }

      this.checkMeteredWeight(pumpData, target, initialPumpValue, data, () => {
        this.batchMetered(pumpData, data, initialPumpValue, onFinish);
      }, () => {
        if (materialDetails.batchMode === BatchMode.Manual) {
          this.onPumpFinishFeed(pumpData, data, initialPumpValue, onFinish);
        }
      });

      this.checkPumpFinishFeed(pumpData, data, (pumpData: IMeteredDataToBatch, data: IBatchData) => {
        this.onPumpFinishFeed(pumpData, data, initialPumpValue, onFinish);
      });

    }, (batchDelay || 1) * 1000, `Batch metered with delay - pump #${pump.id}`);
  },

};
